/**
 * Patient profile builder — admin creates a full patient profile directly,
 * bypassing self-service signup, one profile at a time (CarePortals bulk
 * import is disabled server-side). Phone is required for every profile
 * regardless of mode; address is only required in customer/migration mode
 * (something to ship). Two modes:
 *   - Customer / migration: demographics + optional product
 *     pre-assignment (seeds a suggested cart the patient's own portal
 *     hydrates from on next login — no charge happens here).
 *   - New hire: demographics only, no products, no address required.
 *     Granting admin access afterward is the separate, already-existing
 *     Admins screen.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useMarketplace } from '@/lib/api/hooks';
import {
  useCreatePatientProfile,
  useRecentPatientProfiles,
  useSendProfileInvite,
} from '@/lib/api/patientProfileHooks';
import type { ProductLineIn, RecentProfile } from '@/lib/api/patientProfileHooks';
import {
  CREATE_PROFILE_REASON_CODES,
  profileReasonCodeLabel,
} from '@/lib/patientProfileReasons';
import { US_STATES } from '@/lib/usStates';

import { Badge, fmtDateTime } from './clinical-admin/ui';
import { isReasonValid, ReasonPicker } from './clinical-admin/ReasonPicker';
import type { ReasonValue } from './clinical-admin/ReasonPicker';

type Mode = 'customer' | 'new_hire';

export function PatientProfileBuilderScreen() {
  const [mode, setMode] = useState<Mode>('customer');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [sex, setSex] = useState<'male' | 'female' | 'other'>('male');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [cartNote, setCartNote] = useState('');
  const [reasonValue, setReasonValue] = useState<ReasonValue>({
    reasonCode: '',
    reason: '',
  });

  const marketplace = useMarketplace();
  const create = useCreatePatientProfile();
  const sendInvite = useSendProfileInvite();
  const recentProfiles = useRecentPatientProfiles();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  // One unit per product: the patient's cart can't hold more.
  const lines: ProductLineIn[] = useMemo(
    () => Array.from(selectedProducts, (product_id) => ({ product_id, quantity: 1 as const })),
    [selectedProducts],
  );

  const hasAddress = line1.trim() && city.trim() && state.trim() && postalCode.trim();
  // Address ships products, so it's only required in customer/migration
  // mode — New Hire has no products and nothing to ship.
  const addressRequired = mode === 'customer';
  const canSubmit =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    phone.trim() &&
    (!addressRequired || hasAddress) &&
    isReasonValid(reasonValue) &&
    !create.isPending;

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setDob('');
    setSex('male');
    setLine1('');
    setLine2('');
    setCity('');
    setState('');
    setPostalCode('');
    setSelectedProducts(new Set());
    setCartNote('');
    setReasonValue({ reasonCode: '', reason: '' });
    // Clear the previous profile's result so its panel and Send invite
    // button can't be mistaken for (or sent to) the next one.
    create.reset();
    sendInvite.reset();
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    await create.mutateAsync({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      date_of_birth: dob || undefined,
      sex,
      address: hasAddress
        ? {
            line1: line1.trim(),
            line2: line2.trim() || undefined,
            city: city.trim(),
            state: state.trim(),
            postal_code: postalCode.trim(),
          }
        : undefined,
      reason_code: reasonValue.reasonCode,
      reason: reasonValue.reason.trim() || undefined,
      product_lines: mode === 'customer' && lines.length ? lines : undefined,
      cart_note: mode === 'customer' ? cartNote.trim() || undefined : undefined,
    });
  };

  const created = create.data;

  return (
    <div className="flex flex-col gap-24 max-w-content-md">
      <div className="flex flex-col gap-8">
        <h1 className="type-heading-xl">Create patient profile</h1>
        <p className="type-body-md text-ink-muted">
          Creates a Supabase login + Healthie chart + patients row directly —
          no self-signup required. For new hires, grant admin access
          afterward on the{' '}
          <Link to="/admin/admins" className="underline">
            Admins
          </Link>{' '}
          screen; this tool only creates the profile.
        </p>
      </div>

      <div className="flex gap-8">
        <ModeButton active={mode === 'customer'} onClick={() => setMode('customer')}>
          Customer / migration
        </ModeButton>
        <ModeButton active={mode === 'new_hire'} onClick={() => setMode('new_hire')}>
          New hire (no products)
        </ModeButton>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-16">
        <div className="p-16 border border-line rounded-lg bg-raised flex flex-col gap-12">
          <h2 className="type-heading-md">Demographics</h2>
          <div className="grid grid-cols-2 gap-10">
            <Field label="First name" required>
              <input
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Last name" required>
              <input
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Email" required>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Phone" required>
              <input
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Date of birth">
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Sex">
              <select
                value={sex}
                onChange={(e) => setSex(e.target.value as typeof sex)}
                className={inputClass}
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </Field>
          </div>

          <span className="type-label-sm text-ink-muted mt-4">
            Address{!addressRequired && ' (optional — no products to ship)'}
          </span>
          <div className="grid grid-cols-2 gap-10">
            <Field label="Line 1" required={addressRequired}>
              <input
                required={addressRequired}
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Line 2 (apt, suite, etc.)">
              <input
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="City" required={addressRequired}>
              <input
                required={addressRequired}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="State" required={addressRequired}>
              <select
                required={addressRequired}
                value={state}
                onChange={(e) => setState(e.target.value)}
                className={inputClass}
              >
                <option value="">Select a state…</option>
                {US_STATES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Postal code" required={addressRequired}>
              <input
                required={addressRequired}
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
        </div>

        {mode === 'customer' && (
          <div className="p-16 border border-line rounded-lg bg-raised flex flex-col gap-12">
            <div className="flex flex-col gap-4">
              <h2 className="type-heading-md">Assign products (optional)</h2>
              <p className="type-body-sm text-ink-muted">
                Seeds a suggested cart — nothing is charged here. The
                products pre-fill the patient's own cart the next time they
                log in, and they complete a normal checkout + intake
                themselves.
              </p>
            </div>
            {marketplace.isLoading && (
              <p className="type-label-sm text-ink-muted">Loading products…</p>
            )}
            {marketplace.data && (
              <div className="flex flex-col gap-6 max-h-[16rem] overflow-y-auto">
                {marketplace.data.products.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-8 type-label-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProducts.has(p.id)}
                      onChange={(e) =>
                        setSelectedProducts((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(p.id);
                          else next.delete(p.id);
                          return next;
                        })
                      }
                    />
                    <span className="flex-1">{p.title}</span>
                  </label>
                ))}
              </div>
            )}
            {lines.length > 0 && (
              <Field label="Note for this suggested cart (optional)">
                <input
                  value={cartNote}
                  onChange={(e) => setCartNote(e.target.value)}
                  placeholder="e.g. matches their active CarePortals treatments"
                  className={inputClass}
                />
              </Field>
            )}
          </div>
        )}

        <div className="p-16 border border-line rounded-lg bg-raised flex flex-col gap-8">
          <h2 className="type-heading-md">Why</h2>
          <ReasonPicker
            codes={CREATE_PROFILE_REASON_CODES}
            labelFor={profileReasonCodeLabel}
            value={reasonValue}
            onChange={setReasonValue}
            disabled={create.isPending}
            label="Reason (required)"
          />
        </div>

        <div className="flex items-center gap-10">
          <button
            type="submit"
            disabled={!canSubmit}
            className="h-36 px-15 bg-ink text-page type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {create.isPending ? 'Creating…' : 'Create profile'}
          </button>
          {create.isError && (
            <span className="type-label-sm text-danger">
              {create.error instanceof Error
                ? create.error.message
                : 'Could not create profile.'}
            </span>
          )}
        </div>
      </form>

      {created && (
        <div className="p-16 border border-line rounded-lg bg-raised flex items-center justify-between gap-12">
          <div className="flex flex-col gap-2">
            <span className="type-label-md">
              {created.resumed ? 'Profile updated for' : 'Profile created for'}{' '}
              {created.display_name || created.email}.
            </span>
            {created.warnings.map((w) => (
              <span key={w} className="type-label-sm text-danger">
                {w}
              </span>
            ))}
            <span className="type-label-sm text-ink-muted">
              No login email sent yet — send it whenever you're ready.
            </span>
          </div>
          <div className="flex items-center gap-8">
            <button
              type="button"
              disabled={sendInvite.isPending}
              onClick={() => sendInvite.mutate(created.id)}
              className="h-32 px-12 bg-sunken text-ink type-label-sm rounded hover:opacity-90 disabled:opacity-40"
            >
              {sendInvite.isPending ? 'Sending…' : 'Send invite'}
            </button>
            <button
              type="button"
              onClick={reset}
              className="h-32 px-12 border border-line type-label-sm rounded hover:bg-sunken"
            >
              Create another
            </button>
          </div>
        </div>
      )}
      {sendInvite.isSuccess && (
        <p className="type-label-sm text-ink-muted">
          {sendInvite.data.sent
            ? 'Invite sent.'
            : "Invite not sent. Check the patient's email and that email sending is configured."}
        </p>
      )}

      <RecentlyCreatedSection
        profiles={recentProfiles.data?.profiles ?? []}
        isLoading={recentProfiles.isLoading}
        selectedIds={selectedIds}
        onToggle={(id) =>
          setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          })
        }
        onToggleAll={(ids, checked) =>
          setSelectedIds((prev) => {
            const next = new Set(prev);
            for (const id of ids) {
              if (checked) next.add(id);
              else next.delete(id);
            }
            return next;
          })
        }
        bulkSending={bulkSending}
        bulkResult={bulkResult}
        onSendSelected={async () => {
          setBulkResult(null);
          setBulkSending(true);
          const ids = Array.from(selectedIds);
          let sent = 0;
          let failed = 0;
          for (const id of ids) {
            try {
              const result = await sendInvite.mutateAsync(id);
              if (result.sent) sent += 1;
              else failed += 1;
            } catch {
              failed += 1;
            }
          }
          setBulkSending(false);
          setSelectedIds(new Set());
          setBulkResult(
            failed > 0
              ? `Sent ${sent} invite(s); ${failed} failed.`
              : `Sent ${sent} invite(s).`,
          );
        }}
      />
    </div>
  );
}

function RecentlyCreatedSection({
  profiles,
  isLoading,
  selectedIds,
  onToggle,
  onToggleAll,
  bulkSending,
  bulkResult,
  onSendSelected,
}: {
  profiles: RecentProfile[];
  isLoading: boolean;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (ids: string[], checked: boolean) => void;
  bulkSending: boolean;
  bulkResult: string | null;
  onSendSelected: () => void;
}) {
  const allIds = profiles.map((p) => p.patient_id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const anySelected = selectedIds.size > 0;

  return (
    <div className="border border-line rounded-lg bg-raised overflow-hidden">
      <div className="p-16 border-b border-line flex items-center justify-between gap-12">
        <div className="flex flex-col gap-2">
          <h2 className="type-heading-md">Recently created</h2>
          <p className="type-body-sm text-ink-muted">
            Select profiles to send (or re-send) their login invite.
          </p>
        </div>
        <button
          type="button"
          disabled={!anySelected || bulkSending}
          onClick={onSendSelected}
          className="h-32 px-12 bg-ink text-page type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {bulkSending
            ? 'Sending…'
            : `Send invites${anySelected ? ` (${selectedIds.size})` : ''}`}
        </button>
      </div>

      {isLoading && (
        <div className="p-16 text-center text-ink-muted type-label-md">
          Loading…
        </div>
      )}
      {bulkResult && (
        <div className="px-16 py-8 type-label-sm text-ink-muted border-b border-line">
          {bulkResult}
        </div>
      )}

      {!isLoading && profiles.length === 0 && (
        <div className="p-16 text-center text-ink-muted type-label-sm">
          No profiles created yet.
        </div>
      )}

      {profiles.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full type-label-sm">
            <thead className="bg-sunken border-b border-line text-left text-ink-muted">
              <tr>
                <th className="px-12 py-8 font-medium">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => onToggleAll(allIds, e.target.checked)}
                  />
                </th>
                <th className="px-12 py-8 font-medium">Email</th>
                <th className="px-12 py-8 font-medium">Name</th>
                <th className="px-12 py-8 font-medium">Mode</th>
                <th className="px-12 py-8 font-medium">Created</th>
                <th className="px-12 py-8 font-medium">Invite status</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <tr key={p.patient_id} className="border-t border-line-subtle">
                  <td className="px-12 py-8">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p.patient_id)}
                      onChange={() => onToggle(p.patient_id)}
                    />
                  </td>
                  <td className="px-12 py-8">{p.email || '—'}</td>
                  <td className="px-12 py-8">{p.display_name || '—'}</td>
                  <td className="px-12 py-8">
                    {p.reason_code === 'careportals_migration' ? (
                      <Badge tone="accent">CarePortals</Badge>
                    ) : p.mode === 'customer' ? (
                      'Customer'
                    ) : (
                      'New hire'
                    )}
                  </td>
                  <td className="px-12 py-8">{fmtDateTime(p.created_at)}</td>
                  <td className="px-12 py-8">
                    {p.invited_at ? (
                      <Badge tone="good">Sent {fmtDateTime(p.invited_at)}</Badge>
                    ) : (
                      <Badge tone="warn">Not invited</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const inputClass =
  'h-36 px-10 bg-white border border-line text-13 rounded focus:outline-none focus:border-ink w-full';

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-4">
      <span className="font-mono uppercase text-12 text-ink-muted">
        {label}
        {required ? ' *' : ''}
      </span>
      {children}
    </label>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-36 px-15 type-label-sm rounded border ${
        active
          ? 'bg-ink text-page border-ink'
          : 'bg-raised text-ink border-line hover:bg-sunken'
      }`}
    >
      {children}
    </button>
  );
}
